import React, { useState, useEffect, useCallback } from 'react';
import TopUpCreditsModal from './TopUpCreditsModal';
import TrustedLocationsManager from './TrustedLocationsManager';
import FamilyLiveTab from './FamilyLiveTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'members', label: 'Members' },
  { id: 'approvals', label: 'Approvals' },
  { id: 'live', label: 'Live' },
  { id: 'credits', label: 'Credits' },
  { id: 'settings', label: 'Settings' },
];

const MEMBER_TYPES = [
  { value: 'app_user', label: 'Adult (app user)' },
  { value: 'teen', label: 'Teen 13–17 (supervised)' },
  { value: 'no_app', label: 'No smartphone (SMS only)' },
];

const FamilyProfilePage = ({ onBack, user, initialTab }) => {
  const [activeTab, setActiveTab] = useState(initialTab || 'members');
  const [profile, setProfile] = useState({
    family_name: '',
    members: [],
    enabled: false,
    centralized_billing_default: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Approvals
  const [approvals, setApprovals] = useState([]);
  const [approvalsLoading, setApprovalsLoading] = useState(false);

  // Family credits
  const [credits, setCredits] = useState({ balance: 0, history: [] });
  const [showTopUpModal, setShowTopUpModal] = useState(false);

  // Per-member spending lookups
  const [memberSpending, setMemberSpending] = useState({}); // {member_id: {month_spent, remaining, monthly_limit}}

  // Add-member draft
  const [memberDraft, setMemberDraft] = useState({
    name: '', phone: '', relationship: '', member_type: 'app_user',
    age: '', user_id: '', monthly_spending_limit: '', use_primary_payment: true, member_payment_method: 'mobile_money'
  });

  // ----- API helpers -----
  const fetchProfile = useCallback(async () => {
    if (!user?.id) return;
    try {
      const r = await fetch(`${API_URL}/api/settings/family/${user.id}`);
      const d = await r.json();
      if (d?.success && d?.profile) {
        setProfile({
          family_name: d.profile.family_name || '',
          members: d.profile.members || [],
          enabled: !!d.profile.enabled,
          centralized_billing_default: d.profile.centralized_billing_default !== false,
        });
      }
    } catch (_) { /* ignore */ }
    setLoading(false);
  }, [user?.id]);

  const persist = useCallback(async (next) => {
    if (!user?.id) return false;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/settings/family/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(next)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(data?.detail || 'Failed to save');
        return false;
      }
      // Server returns canonical profile (with member_ids assigned). Hydrate state.
      if (data?.profile) {
        setProfile({
          family_name: data.profile.family_name || '',
          members: data.profile.members || [],
          enabled: !!data.profile.enabled,
          centralized_billing_default: data.profile.centralized_billing_default !== false,
        });
      }
      return true;
    } catch (_) {
      return false;
    } finally {
      setSaving(false);
    }
  }, [user?.id]);

  const fetchApprovals = useCallback(async () => {
    if (!user?.id) return;
    setApprovalsLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/family/${user.id}/approvals/pending`);
      const d = await r.json();
      setApprovals(d?.items || []);
    } catch (_) { /* ignore */ }
    setApprovalsLoading(false);
  }, [user?.id]);

  const fetchCredits = useCallback(async () => {
    if (!user?.id) return;
    try {
      const r = await fetch(`${API_URL}/api/family/${user.id}/credits`);
      const d = await r.json();
      if (d?.success) setCredits({ balance: d.balance || 0, history: d.history || [] });
    } catch (_) { /* ignore */ }
  }, [user?.id]);

  const fetchMemberSpending = useCallback(async (memberId) => {
    if (!user?.id || !memberId) return;
    try {
      const r = await fetch(`${API_URL}/api/family/${user.id}/members/${memberId}/spending`);
      const d = await r.json();
      if (d?.success) {
        setMemberSpending(prev => ({ ...prev, [memberId]: d }));
      }
    } catch (_) { /* ignore */ }
  }, [user?.id]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  // Prefetch approvals + credits on mount so badges render from any tab
  useEffect(() => { fetchApprovals(); fetchCredits(); }, [fetchApprovals, fetchCredits]);

  useEffect(() => {
    if (activeTab === 'approvals') {
      fetchApprovals();
      const t = setInterval(fetchApprovals, 15000); // light polling for live updates
      return () => clearInterval(t);
    }
    if (activeTab === 'credits') {
      fetchCredits();
    }
  }, [activeTab, fetchApprovals, fetchCredits]);

  useEffect(() => {
    // After profile loads, fetch spending for any member with a linked user_id and a limit
    profile.members.forEach(m => {
      if (m.member_id && m.user_id && m.monthly_spending_limit) {
        fetchMemberSpending(m.member_id);
      }
    });
    // Intentionally not returning the cleanup; profile changes infrequently
  }, [profile.members, fetchMemberSpending]);

  // ----- Mutations -----
  const handleAddMember = async () => {
    if (!memberDraft.name.trim() || !memberDraft.phone.trim()) {
      alert('Name and phone are required');
      return;
    }
    if (memberDraft.member_type === 'teen') {
      const a = parseInt(memberDraft.age, 10);
      if (!Number.isFinite(a) || a < 13 || a > 17) {
        alert('Teen members must have age between 13 and 17');
        return;
      }
    }
    if (profile.members.length >= 10) {
      alert('Maximum 10 family members allowed');
      return;
    }
    const newMember = {
      name: memberDraft.name.trim(),
      phone: memberDraft.phone.trim(),
      relationship: memberDraft.relationship.trim() || null,
      member_type: memberDraft.member_type,
      age: memberDraft.age ? parseInt(memberDraft.age, 10) : null,
      user_id: memberDraft.user_id.trim() || null,
      monthly_spending_limit: memberDraft.monthly_spending_limit ? parseFloat(memberDraft.monthly_spending_limit) : null,
      use_primary_payment: !!memberDraft.use_primary_payment,
      member_payment_method: memberDraft.use_primary_payment ? null : memberDraft.member_payment_method,
    };
    const next = { ...profile, members: [...profile.members, newMember] };
    const ok = await persist(next);
    if (ok) {
      setMemberDraft({
        name: '', phone: '', relationship: '', member_type: 'app_user',
        age: '', user_id: '', monthly_spending_limit: '', use_primary_payment: true, member_payment_method: 'mobile_money'
      });
    }
  };

  const handleRemoveMember = async (idx) => {
    if (!confirm('Remove this member?')) return; // eslint-disable-line no-alert
    const next = { ...profile, members: profile.members.filter((_, i) => i !== idx) };
    await persist(next);
  };

  const handleUpdateMember = async (idx, patch) => {
    const next = { ...profile, members: profile.members.map((m, i) => i === idx ? { ...m, ...patch } : m) };
    await persist(next);
  };

  const handleToggleEnabled = async () => {
    if (!profile.enabled && !profile.family_name.trim()) {
      alert('Please add a family name first');
      return;
    }
    await persist({ ...profile, enabled: !profile.enabled });
  };

  const handleSaveName = async () => {
    await persist(profile);
  };

  // Approvals
  const handleApprovalDecision = async (approval, decision) => {
    const url = `${API_URL}/api/family/${user.id}/approvals/${approval.id}/${decision}`;
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decided_by: user.id })
      });
      if (res.ok) {
        fetchApprovals();
      } else {
        const d = await res.json().catch(() => ({}));
        alert(d?.detail || 'Action failed');
      }
    } catch (_) { /* ignore */ }
  };

  return (
    <div className="absolute inset-0 bg-white dark:bg-gray-900 z-50 overflow-y-auto" data-testid="family-profile-page">
      {/* Header */}
      <div className="p-4 border-b dark:border-gray-700 flex items-center justify-between">
        <button onClick={onBack} className="text-green-600 dark:text-green-400 flex items-center space-x-2">
          <span>←</span>
          <span>Family Profile</span>
        </button>
        {saving && <span className="text-[11px] text-gray-500" data-testid="family-saving">Saving…</span>}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 px-4 pt-3 pr-20 border-b border-gray-200 dark:border-gray-700 flex-wrap relative z-0">
        {TABS.map(t => (
          <button
            key={t.id}
            data-testid={`family-tab-${t.id}`}
            onClick={() => setActiveTab(t.id)}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === t.id
                ? 'border-rose-600 text-rose-700 dark:text-rose-400'
                : 'border-transparent text-gray-500 dark:text-gray-400'
            }`}
          >
            {t.label}
            {t.id === 'approvals' && approvals.length > 0 && (
              <span data-testid="family-approvals-badge" className="ml-1 inline-flex items-center justify-center bg-rose-600 text-white text-[10px] rounded-full min-w-[16px] h-4 px-1">
                {approvals.length}
              </span>
            )}
            {t.id === 'credits' && credits.balance > 0 && (
              <span data-testid="family-credits-badge" className="ml-1 inline-flex items-center justify-center bg-green-600 text-white text-[10px] rounded-full px-1.5 h-4">
                K{credits.balance.toLocaleString()}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="p-5">
        {loading ? (
          <div className="py-10 text-center text-sm text-gray-500">Loading…</div>
        ) : (
          <>
            {activeTab === 'members' && (
              <MembersTab
                profile={profile}
                memberDraft={memberDraft}
                setMemberDraft={setMemberDraft}
                onAdd={handleAddMember}
                onRemove={handleRemoveMember}
                onUpdate={handleUpdateMember}
                memberSpending={memberSpending}
              />
            )}
            {activeTab === 'approvals' && (
              <ApprovalsTab
                items={approvals}
                loading={approvalsLoading}
                onDecide={handleApprovalDecision}
              />
            )}
            {activeTab === 'live' && (
              <FamilyLiveTab primaryId={user?.id} />
            )}
            {activeTab === 'credits' && (
              <CreditsTab credits={credits} onTopUp={() => setShowTopUpModal(true)} />
            )}
            {activeTab === 'settings' && (
              <SettingsTab
                profile={profile}
                setProfile={setProfile}
                onToggleEnabled={handleToggleEnabled}
                onSaveName={handleSaveName}
                persist={persist}
              />
            )}
          </>
        )}
      </div>

      {showTopUpModal && (
        <TopUpCreditsModal
          title="Top up Family Credit"
          currentBalance={credits.balance}
          endpoint={`${API_URL}/api/family/${user.id}/credits/top-up`}
          phoneSuggestion={user?.phone}
          onSuccess={(data) => {
            setShowTopUpModal(false);
            // Optimistically update + re-fetch authoritative
            if (data?.balance !== undefined) {
              setCredits(prev => ({ ...prev, balance: data.balance }));
            }
            fetchCredits();
          }}
          onClose={() => setShowTopUpModal(false)}
        />
      )}
    </div>
  );
};

// =====================
// Members tab
// =====================
const MembersTab = ({ profile, memberDraft, setMemberDraft, onAdd, onRemove, onUpdate, memberSpending }) => {
  const atCap = profile.members.length >= 10;
  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          Members ({profile.members.length}/10)
        </h3>
      </div>

      {profile.members.length === 0 ? (
        <p className="text-xs text-gray-500 italic mb-3">No members added yet.</p>
      ) : (
        <div className="space-y-2 mb-4">
          {profile.members.map((m, idx) => (
            <MemberRow
              key={m.member_id || idx}
              member={m}
              spending={m.member_id ? memberSpending[m.member_id] : null}
              onRemove={() => onRemove(idx)}
              onUpdate={(patch) => onUpdate(idx, patch)}
            />
          ))}
        </div>
      )}

      {/* Add-member form */}
      {!atCap && (
        <div className="border border-dashed border-gray-300 dark:border-gray-700 rounded-xl p-4 space-y-2">
          <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300">Add a member</h4>

          <input type="text" value={memberDraft.name} onChange={e => setMemberDraft({ ...memberDraft, name: e.target.value })} placeholder="Full name" data-testid="family-member-name-input"
            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm dark:bg-gray-800 dark:text-white" />
          <input type="tel" value={memberDraft.phone} onChange={e => setMemberDraft({ ...memberDraft, phone: e.target.value })} placeholder="Phone (e.g. 0991234567)" data-testid="family-member-phone-input"
            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm dark:bg-gray-800 dark:text-white" />
          <input type="text" value={memberDraft.relationship} onChange={e => setMemberDraft({ ...memberDraft, relationship: e.target.value })} placeholder="Relationship (optional)" data-testid="family-member-relationship-input"
            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm dark:bg-gray-800 dark:text-white" />

          <select value={memberDraft.member_type} onChange={e => setMemberDraft({ ...memberDraft, member_type: e.target.value })} data-testid="family-member-type-select"
            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm dark:bg-gray-800 dark:text-white">
            {MEMBER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>

          {memberDraft.member_type === 'teen' && (
            <input type="number" value={memberDraft.age} onChange={e => setMemberDraft({ ...memberDraft, age: e.target.value })} placeholder="Age (13-17)" min={13} max={17} data-testid="family-member-age-input"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm dark:bg-gray-800 dark:text-white" />
          )}

          {memberDraft.member_type !== 'no_app' && (
            <input type="text" value={memberDraft.user_id} onChange={e => setMemberDraft({ ...memberDraft, user_id: e.target.value })} placeholder="Linked rider account user_id (optional)" data-testid="family-member-userid-input"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-xs dark:bg-gray-800 dark:text-white font-mono" />
          )}

          <input type="number" value={memberDraft.monthly_spending_limit} onChange={e => setMemberDraft({ ...memberDraft, monthly_spending_limit: e.target.value })} placeholder="Monthly spending limit MWK (optional)" min={0} data-testid="family-member-limit-input"
            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm dark:bg-gray-800 dark:text-white" />

          <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
            <input type="checkbox" checked={memberDraft.use_primary_payment} onChange={e => setMemberDraft({ ...memberDraft, use_primary_payment: e.target.checked })} data-testid="family-member-billing-toggle" />
            <span>Charge to my (primary) payment method</span>
          </label>

          {!memberDraft.use_primary_payment && (
            <select value={memberDraft.member_payment_method} onChange={e => setMemberDraft({ ...memberDraft, member_payment_method: e.target.value })} data-testid="family-member-paymethod-select"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm dark:bg-gray-800 dark:text-white">
              <option value="mobile_money">Mobile Money</option>
              <option value="cash">Cash</option>
            </select>
          )}

          <button
            onClick={onAdd}
            disabled={!memberDraft.name.trim() || !memberDraft.phone.trim()}
            data-testid="family-add-member-btn"
            className="w-full bg-rose-600 hover:bg-rose-700 disabled:bg-rose-300 disabled:cursor-not-allowed text-white rounded-lg py-2 text-sm font-medium"
          >
            Add member
          </button>
        </div>
      )}
    </>
  );
};

const MemberRow = ({ member, spending, onRemove, onUpdate }) => {
  const [expanded, setExpanded] = useState(false);
  const overLimit = spending && spending.monthly_limit && spending.month_spent >= spending.monthly_limit;

  return (
    <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3" data-testid="family-member-row">
      <div className="flex items-center justify-between gap-2">
        <button onClick={() => setExpanded(v => !v)} className="text-left min-w-0 flex-1">
          <p className="font-medium text-gray-900 dark:text-white truncate">
            {member.name}
            <span className="ml-1.5 text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
              {member.member_type === 'teen' ? 'Teen' : member.member_type === 'no_app' ? 'No-app' : 'Adult'}
            </span>
          </p>
          <p className="text-xs text-gray-500">{member.phone}{member.relationship ? ` · ${member.relationship}` : ''}{member.age ? ` · ${member.age}y` : ''}</p>
          {spending && spending.monthly_limit ? (
            <p className={`text-[11px] mt-0.5 ${overLimit ? 'text-red-600' : 'text-gray-500'}`} data-testid="family-member-spending">
              K{spending.month_spent.toLocaleString()} / K{spending.monthly_limit.toLocaleString()} this month
              {overLimit && ' · over limit'}
            </p>
          ) : null}
        </button>
        <button onClick={onRemove} className="text-xs text-red-600 hover:text-red-700 px-2 py-1" data-testid="family-member-remove">
          Remove
        </button>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 space-y-2 text-xs">
          <div>
            <label className="block text-gray-600 dark:text-gray-400 mb-0.5">Monthly spending limit (MWK)</label>
            <input type="number" min={0} defaultValue={member.monthly_spending_limit || ''} onBlur={e => onUpdate({ monthly_spending_limit: e.target.value ? parseFloat(e.target.value) : null })}
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1 dark:bg-gray-900 dark:text-white" data-testid="family-member-limit-edit" />
          </div>
          <div>
            <label className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
              <input type="checkbox" defaultChecked={member.use_primary_payment !== false} onChange={e => onUpdate({ use_primary_payment: e.target.checked })} data-testid="family-member-billing-edit" />
              Charge to primary payment method
            </label>
          </div>
          <TrustedLocationsManager
            locations={member.trusted_locations || []}
            strict={!!member.trusted_locations_strict}
            onChange={(next) => onUpdate({ trusted_locations: next })}
            onToggleStrict={(val) => onUpdate({ trusted_locations_strict: val })}
          />
        </div>
      )}
    </div>
  );
};

// =====================
// Approvals tab
// =====================
const ApprovalsTab = ({ items, loading, onDecide }) => {
  if (loading) {
    return <div className="py-10 text-center text-sm text-gray-500">Loading approvals…</div>;
  }
  if (items.length === 0) {
    return (
      <div className="py-10 text-center" data-testid="approvals-empty">
        <p className="text-sm text-gray-500">No pending approvals.</p>
        <p className="text-xs text-gray-400 mt-1">Teen ride requests and over-limit rides will show up here.</p>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {items.map(it => (
        <div key={it.id} className="border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/10 rounded-xl p-4" data-testid="approval-row">
          <div className="flex items-center justify-between mb-1">
            <p className="font-semibold text-gray-900 dark:text-white">{it.member_name}</p>
            <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-rose-600 text-white">
              {it.kind === 'spending_limit_override' ? 'Over limit' : 'Teen ride'}
            </span>
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-300 mb-1">
            <span className="font-medium">From:</span> {it.ride_summary?.pickup_address || '—'}
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-300 mb-1">
            <span className="font-medium">To:</span> {it.ride_summary?.dropoff_address || '—'}
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-300 mb-3">
            <span className="font-medium">Fare:</span> K{(it.ride_summary?.estimated_price || 0).toLocaleString()} · {it.ride_summary?.vehicle_type}
          </p>
          {it.kind === 'spending_limit_override' && (
            <p className="text-[11px] text-rose-700 dark:text-rose-300 mb-3">
              Already spent K{(it.current_spend || 0).toLocaleString()} of K{(it.monthly_limit || 0).toLocaleString()} this month
            </p>
          )}
          <div className="flex gap-2">
            <button
              onClick={() => onDecide(it, 'approve')}
              data-testid="approval-approve-btn"
              className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg text-sm font-medium"
            >
              Approve
            </button>
            <button
              onClick={() => onDecide(it, 'decline')}
              data-testid="approval-decline-btn"
              className="flex-1 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white py-2 rounded-lg text-sm font-medium"
            >
              Decline
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

// =====================
// Credits tab
// =====================
const CreditsTab = ({ credits, onTopUp }) => {
  const fmtDate = (s) => s ? new Date(s).toLocaleString() : '—';
  const STATUS_PILL = {
    held: 'bg-amber-100 text-amber-800',
    released: 'bg-blue-100 text-blue-800',
    credited: 'bg-green-100 text-green-800',
    consumed: 'bg-gray-200 text-gray-700',
  };
  return (
    <div data-testid="family-credits-panel">
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-xl p-5 mb-4">
        <p className="text-xs uppercase tracking-wider text-green-700 dark:text-green-300 font-semibold">Available Family Credit</p>
        <p className="text-3xl font-bold text-green-700 dark:text-green-300 mt-1" data-testid="family-credit-balance">
          K{(credits.balance || 0).toLocaleString()}
        </p>
        <p className="text-[11px] text-green-700/70 dark:text-green-300/80 mt-2">
          Credit is generated when an approved & prepaid family ride is cancelled, or by topping up via mobile money.
          Toggle <strong>“Use family credit”</strong> at booking to redeem (manual).
        </p>
        <button
          onClick={onTopUp}
          data-testid="family-topup-btn"
          className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-lg"
        >
          + Add Credit (Mobile Money)
        </button>
      </div>

      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Prepayment ledger</h3>
      {credits.history && credits.history.length > 0 ? (
        <div className="space-y-2">
          {credits.history.map((h) => (
            <div key={h.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 text-xs" data-testid="family-credit-row">
              <div className="flex items-center justify-between mb-0.5">
                <span className="font-semibold text-gray-900 dark:text-white">K{(h.amount || 0).toLocaleString()}</span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${STATUS_PILL[h.status] || 'bg-gray-200 text-gray-700'}`}>
                  {h.status}
                </span>
              </div>
              <p className="text-gray-500 dark:text-gray-400">
                {h.member_name || '—'} · ride <span className="font-mono">{(h.ride_id || '').slice(0, 8)}</span>
              </p>
              <p className="text-[10px] text-gray-400 mt-0.5">
                {h.status === 'held' && `Held ${fmtDate(h.created_at)}`}
                {h.status === 'released' && `Released ${fmtDate(h.released_at)}`}
                {h.status === 'credited' && `Credited ${fmtDate(h.credited_at)} · ${h.credit_reason || ''}`}
                {h.status === 'consumed' && `Used ${fmtDate(h.consumed_at)} on ride ${(h.consumed_in_ride_id || '').slice(0, 8)}`}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-gray-500 italic">No prepayments yet.</p>
      )}
    </div>
  );
};

// =====================
// Settings tab
// =====================
const SettingsTab = ({ profile, setProfile, onToggleEnabled, onSaveName, persist }) => (
  <div className="space-y-4">
    {/* Enable toggle */}
    <div className="flex items-center justify-between bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-xl p-4">
      <div>
        <p className="font-semibold text-gray-900 dark:text-white">Family Sharing</p>
        <p className="text-xs text-gray-600 dark:text-gray-400">Allow members to ride under this profile</p>
      </div>
      <button
        onClick={onToggleEnabled}
        data-testid="family-enable-toggle"
        className={`relative w-12 h-6 rounded-full transition-colors ${profile.enabled ? 'bg-rose-600' : 'bg-gray-300 dark:bg-gray-600'}`}
        aria-pressed={profile.enabled}
      >
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${profile.enabled ? 'translate-x-6' : ''}`} />
      </button>
    </div>

    {/* Family name */}
    <div>
      <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">Family Name</label>
      <input
        type="text"
        value={profile.family_name}
        onChange={e => setProfile({ ...profile, family_name: e.target.value })}
        onBlur={onSaveName}
        placeholder="e.g. The Banda Family"
        data-testid="family-name-input"
        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-rose-500"
      />
    </div>

    {/* Centralized billing default */}
    <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
      <div>
        <p className="font-semibold text-gray-900 dark:text-white text-sm">Centralized billing default</p>
        <p className="text-xs text-gray-600 dark:text-gray-400">New members will be charged to your payment method by default. Override per-member.</p>
      </div>
      <button
        onClick={() => persist({ ...profile, centralized_billing_default: !profile.centralized_billing_default })}
        data-testid="family-billing-default-toggle"
        className={`relative w-12 h-6 rounded-full transition-colors ${profile.centralized_billing_default ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'}`}
        aria-pressed={profile.centralized_billing_default}
      >
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${profile.centralized_billing_default ? 'translate-x-6' : ''}`} />
      </button>
    </div>

    <p className="text-xs text-gray-500 leading-relaxed">
      Up to 10 members. Teen members (13–17) need your approval for each ride. Members with a monthly spending limit get a soft block when they exceed it — you can approve overrides from the Approvals tab.
    </p>
  </div>
);

export default FamilyProfilePage;
